import { useEffect, useState } from 'react';
import api, { errorMessage } from '../../services/api';
import { useAuth } from '../../services/auth';
import { PageNav, PageAlert, Modal, inputStyle } from './common';

const accent = '#1565c0';
const cardStyle = { borderRadius: '12px', background: '#ffffff', border: '1px solid #dee2e6' };

function QuotaBadge({ q }) {
  if (q.max_quantity === null) return <span className="badge bg-light text-dark border">Sans quota</span>;
  const used = q.used_quantity + q.reserved_quantity;
  const pct = q.max_quantity ? (used / q.max_quantity) * 100 : 100;
  if (pct >= 100) return <span className="badge bg-danger">Épuisé</span>;
  if (pct >= 80) return <span className="badge bg-warning text-dark">Critique</span>;
  if (pct >= 50) return <span className="badge bg-info text-dark">Moyen</span>;
  return <span className="badge bg-success">OK</span>;
}

function QuotaBar({ q }) {
  if (q.max_quantity === null) return <span className="text-muted">—</span>;
  const used = q.used_quantity + q.reserved_quantity;
  const pct = Math.min(q.max_quantity ? (used / q.max_quantity) * 100 : 100, 100);
  const color = pct >= 100 ? 'bg-danger' : pct >= 80 ? 'bg-warning' : pct >= 50 ? 'bg-info' : 'bg-success';
  return <div className="progress" style={{ height: '6px', backgroundColor: '#dee2e6' }}><div className={`progress-bar ${color}`} style={{ width: `${pct}%` }}></div></div>;
}

export default function DailyQuotas() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [quotasData, setQuotasData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [edit, setEdit] = useState(null);

  useEffect(() => {
    api.get('/palet/admin/sessions').then((r) => setSessions(r.data))
      .catch(() => setMessage({ type: 'danger', text: 'Erreur lors du chargement des sessions' }));
  }, []);

  const loadQuotas = async (id) => {
    if (!id) { setQuotasData(null); return; }
    setLoading(true);
    try { setQuotasData((await api.get(`/palet/admin/daily-quotas/${id}`)).data); }
    catch { setMessage({ type: 'danger', text: 'Erreur lors du chargement des quotas' }); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      await api.put(`/palet/admin/daily-quota/${quotasData.session_id}/${edit.prize_config_id}`, {
        quota_date: edit.date, max_quantity: edit.unlimited ? null : edit.max_quantity, all_days: edit.all_days,
      });
      setMessage({ type: 'success', text: 'Quota mis à jour avec succès' });
      setEdit(null);
      loadQuotas(quotasData.session_id);
    } catch (e) {
      setMessage({ type: 'danger', text: errorMessage(e, 'Erreur lors de la mise à jour') });
    }
  };

  const day = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-vh-100" style={{ background: '#f4f6f9' }}>
      <PageNav user={user} title="Quotas" />
      <div className="container py-4">
        <PageAlert message={message} onClose={() => setMessage({ type: '', text: '' })} />
        <div className="mb-4">
          <h2 className="fw-bold mb-1" style={{ color: '#1a1a1a' }}><i className="fas fa-calendar-alt me-2" style={{ color: accent }}></i>Gestion des Quotas Journaliers</h2>
          <p className="mb-0" style={{ color: '#555' }}>{sessions.length} session(s) configurée(s) · un quota limite le nombre d'un lot gagnable par jour (le stock reste la limite globale).</p>
        </div>

        <div className="card border-0 shadow mb-4" style={cardStyle}>
          <div className="card-body p-3">
            <label className="form-label fw-bold" htmlFor="q-session" style={{ color: '#333' }}>Sélectionner une session</label>
            <select id="q-session" className="form-select" value={selectedId} style={inputStyle}
              onChange={(e) => { setSelectedId(e.target.value); loadQuotas(e.target.value); }}>
              <option value="">-- Choisir une session --</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>{s.pdv_name} - {s.name} ({s.start_time?.split('T')[0]} au {s.end_time?.split('T')[0]})</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5"><div className="spinner-border" style={{ color: accent }}></div></div>
        ) : !quotasData ? (
          <div className="card border-0 shadow text-center p-5" style={cardStyle}>
            <i className="fas fa-calendar-plus fa-4x mb-3" style={{ opacity: 0.4, color: accent }}></i>
            <p className="mb-0" style={{ color: '#555' }}>Sélectionnez une session pour voir les quotas journaliers</p>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <h5 style={{ color: '#1a1a1a' }}><i className="fas fa-store me-2" style={{ color: accent }}></i>{quotasData.pdv_name}</h5>
              <p className="mb-0" style={{ color: '#555' }}><small>Du {quotasData.start_date} au {quotasData.end_date}</small></p>
            </div>
            {quotasData.quotas_by_date.map((d) => (
              <div key={d.date} className="card border-0 shadow mb-4" style={{ ...cardStyle, borderColor: d.is_today ? accent : '#dee2e6' }}>
                <div className="card-header p-3" style={{ background: d.is_today ? 'rgba(66,165,245,0.2)' : 'rgba(66,165,245,0.1)', borderBottom: '1px solid #dee2e6' }}>
                  <h6 className="mb-0 fw-bold" style={{ color: accent }}>
                    <i className="fas fa-calendar-day me-2"></i>{day(d.date)}{d.is_today && <span className="badge bg-primary ms-2">Aujourd'hui</span>}
                  </h6>
                </div>
                <div className="card-body p-3">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>{['Lot', 'Quota', 'Gagnés', 'Réservés', 'Restant', 'Statut', 'Progression', 'Action'].map((h, i) => (
                          <th key={h} className={i ? 'text-center' : ''} style={{ color: accent }}>{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {d.quotas.map((q) => (
                          <tr key={q.prize_config_id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div style={{ width: '16px', height: '16px', backgroundColor: q.color, borderRadius: '4px', marginRight: '10px' }}></div>
                                <div><div className="fw-semibold">{q.prize_label}</div><small className="text-muted">{q.prize_name}</small></div>
                              </div>
                            </td>
                            <td className="text-center">{q.max_quantity ?? '∞'}</td>
                            <td className="text-center">{q.used_quantity}</td>
                            <td className="text-center text-muted">{q.reserved_quantity}</td>
                            <td className="text-center fw-bold" style={{ color: q.remaining_quantity === 0 ? '#dc3545' : '#28a745' }}>{q.remaining_quantity ?? '—'}</td>
                            <td className="text-center"><QuotaBadge q={q} /></td>
                            <td className="text-center" style={{ minWidth: '120px' }}><QuotaBar q={q} /></td>
                            <td className="text-center">
                              <button className="btn btn-sm" title="Modifier le quota" style={{ background: 'linear-gradient(135deg, #1565c0, #0d47a1)', color: '#fff', border: 'none' }}
                                onClick={() => setEdit({ ...q, date: d.date, unlimited: q.max_quantity === null, max_quantity: q.max_quantity ?? 0, all_days: false })}>
                                <i className="fas fa-edit"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                        <tr style={{ background: '#fff8f8' }}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div style={{ width: '16px', height: '16px', backgroundColor: '#e57373', borderRadius: '4px', marginRight: '10px' }}></div>
                              <div><div className="fw-semibold" style={{ color: '#c62828' }}>Sans lot</div><small className="text-muted">Lancers perdants</small></div>
                            </div>
                          </td>
                          <td className="text-center text-muted">—</td>
                          <td className="text-center fw-bold" style={{ color: '#c62828' }}>{d.try_again_count}</td>
                          <td className="text-center text-muted" colSpan="5">—</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {edit && (
        <Modal title="Modifier le quota journalier" icon="fas fa-edit" onClose={() => setEdit(null)}
          footer={<>
            <button type="button" className="btn btn-secondary" onClick={() => setEdit(null)}>Annuler</button>
            <button type="button" className="btn btn-primary" onClick={handleSave} style={{ background: 'linear-gradient(135deg, #1976d2, #1565c0)', border: 'none' }}>
              <i className="fas fa-save me-2"></i>Enregistrer
            </button>
          </>}>
          <div className="mb-3"><label className="form-label" style={{ color: '#333' }}>Date</label><input type="date" className="form-control" value={edit.date} disabled style={inputStyle} /></div>
          <div className="mb-3"><label className="form-label" style={{ color: '#333' }}>Lot</label><input className="form-control" value={edit.prize_label} disabled style={inputStyle} /></div>
          <div className="form-check mb-2">
            <input id="q-unl" className="form-check-input" type="checkbox" checked={edit.unlimited} onChange={(e) => setEdit({ ...edit, unlimited: e.target.checked })} />
            <label className="form-check-label" htmlFor="q-unl">Pas de quota (limité par le stock seulement)</label>
          </div>
          {!edit.unlimited && (
            <div className="mb-3">
              <label className="form-label" htmlFor="q-max" style={{ color: '#333' }}>Quota maximum</label>
              <input id="q-max" type="number" min="0" className="form-control" value={edit.max_quantity} onChange={(e) => setEdit({ ...edit, max_quantity: parseInt(e.target.value, 10) || 0 })} style={inputStyle} />
              <small className="text-muted">Nombre maximum de ce lot gagnable pour cette journée</small>
            </div>
          )}
          <div className="form-check mb-3">
            <input id="q-all" className="form-check-input" type="checkbox" checked={edit.all_days} onChange={(e) => setEdit({ ...edit, all_days: e.target.checked })} />
            <label className="form-check-label" htmlFor="q-all">Appliquer à tous les jours de la session</label>
          </div>
          <div className="alert alert-info mb-0" style={{ background: 'rgba(21,101,192,0.08)', borderColor: 'rgba(21,101,192,0.2)' }}>
            <small style={{ color: '#555' }}><i className="fas fa-info-circle me-2"></i>
              Gagnés ce jour : <strong>{edit.used_quantity}</strong>{edit.reserved_quantity ? <> · réservés : <strong>{edit.reserved_quantity}</strong></> : null}.
              Quand le quota est atteint, ce lot n'est plus placé dans les cases jusqu'au lendemain.
            </small>
          </div>
        </Modal>
      )}
    </div>
  );
}
